<template>
  <div class="traces">
    <!-- Toolbar: time range + live toggle (mirrors MetricsView) -->
    <div class="toolbar">
      <div class="toolbar-spacer" />

      <TimeRangePicker :model-value="range" @update:model-value="setRange" />

      <LgTooltip :text="liveTitle" placement="bottom">
        <LgButton
          class="live-toggle"
          :class="live ? 'live' : 'off'"
          size="sm"
          variant="ghost"
          :aria-pressed="live"
          @click="live = !live"
        >
          <template #icon>
            <Play v-if="live" :size="14" aria-hidden="true" />
            <Pause v-else :size="14" aria-hidden="true" />
          </template>
          {{ live ? 'Live' : 'Paused' }}
        </LgButton>
      </LgTooltip>
    </div>

    <!-- Filter row -->
    <div class="filter-row">
      <LgSelect
        v-model="service"
        class="service-select"
        :options="serviceOptions"
        aria-label="Filter by service"
      />

      <LgButton
        class="errors-toggle"
        :class="{ on: errorsOnly }"
        size="sm"
        variant="ghost"
        :aria-pressed="errorsOnly"
        title="Only show traces that contain an error"
        @click="errorsOnly = !errorsOnly"
      >
        <template #icon><CircleAlert :size="14" aria-hidden="true" /></template>
        Errors only
      </LgButton>

      <LgInput
        v-model="minDurationText"
        class="min-dur"
        type="number"
        placeholder="Min ms"
        aria-label="Minimum duration in milliseconds"
        title="Hide traces faster than this many milliseconds"
      >
        <template #lead><Timer :size="14" aria-hidden="true" /></template>
      </LgInput>

      <LgInput
        v-model="userText"
        class="user-filter"
        placeholder="User…"
        aria-label="Filter by user id"
      >
        <template #lead><User :size="14" aria-hidden="true" /></template>
      </LgInput>

      <LgInput
        v-model="searchText"
        class="search"
        placeholder="Search traces by name…"
        aria-label="Search traces by root name"
      >
        <template #lead><Search :size="14" aria-hidden="true" /></template>
        <template #trail>
          <LgIconButton
            v-if="searchText"
            class="search-clear"
            label="Clear search"
            size="sm"
            @click="searchText = ''"
          >
            <X :size="14" aria-hidden="true" />
          </LgIconButton>
        </template>
      </LgInput>
    </div>

    <div class="body">
      <!-- Left: trace list -->
      <aside class="list-pane">
        <TraceList
          :traces="traces"
          :selected-id="selected?.traceId ?? null"
          :loading="listLoading"
          :error="listError"
          @pick="onPickTrace"
        />
      </aside>

      <!-- Right: trace detail (full-screen overlay on mobile) -->
      <main class="detail-pane" :class="{ 'mobile-open': !!selected }">
        <template v-if="selected">
          <div class="detail-head">
            <LgButton class="only-mobile back-btn" size="sm" variant="ghost" @click="closeDetail">
              <template #icon><ChevronLeft :size="15" aria-hidden="true" /></template>
              Traces
            </LgButton>

            <div class="detail-title-row">
              <h2 class="detail-title">{{ selected.rootName }}</h2>
              <span class="detail-when">{{ absTime(selected.ts) }}</span>
            </div>
            <div class="detail-stats">
              <span class="stat">
                <span class="stat-k">duration</span>{{ formatDurationNs(selected.durationNs) }}
              </span>
              <span class="stat"> <span class="stat-k">spans</span>{{ selected.spanCount }} </span>
              <span class="stat" :class="{ 'stat-err': selected.errorCount > 0 }">
                <span class="stat-k">errors</span>{{ selected.errorCount }}
              </span>
              <span class="stat"
                ><span class="stat-k">service</span>{{ selected.rootService }}</span
              >
              <span v-if="selected.userId" class="stat">
                <span class="stat-k">user</span>{{ selected.userId }}
              </span>
            </div>

            <div class="detail-tabs" role="tablist" aria-label="Trace detail sections">
              <button
                class="tab"
                :class="{ active: detailTab === 'waterfall' }"
                role="tab"
                :aria-selected="detailTab === 'waterfall'"
                @click="detailTab = 'waterfall'"
              >
                Waterfall
              </button>
              <button
                class="tab"
                :class="{ active: detailTab === 'logs' }"
                role="tab"
                :aria-selected="detailTab === 'logs'"
                @click="detailTab = 'logs'"
              >
                Logs
              </button>
            </div>
          </div>

          <div class="detail-body">
            <template v-if="detailTab === 'waterfall'">
              <div v-if="detailLoading" class="detail-note">Loading spans…</div>
              <div v-else-if="detailError" class="detail-note err">
                Couldn't load this trace.
                <button class="link-btn" @click="loadDetail(selected.traceId)">Retry</button>
              </div>
              <TraceWaterfall v-else :spans="spans" />
            </template>
            <TraceCorrelatedLogs
              v-else
              :trace-id="selected.traceId"
              @unauthorized="emit('unauthorized')"
            />
          </div>
        </template>

        <!-- Nothing selected yet (desktop) -->
        <div v-else class="detail-empty">
          <GoblinIcon variant="mark" :size="64" />
          <p class="empty-title">Pick a trace to see its story</p>
          <p class="empty-sub">
            The waterfall shows every step of a request — what ran, in what order, and how long each
            step took.
          </p>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { Play, Pause, Search, X, CircleAlert, Timer, User, ChevronLeft } from 'lucide-vue-next';
import { api, ApiError, type TraceSummary, type TraceSpan } from '../api.js';
import { RANGE_MS, DEFAULT_RANGE, type TimeRange } from '../composables/useFilters.js';
import { formatDurationNs } from '../lib/trace-layout.js';
import TimeRangePicker from '../components/TimeRangePicker.vue';
import TraceList from '../components/traces/TraceList.vue';
import TraceWaterfall from '../components/traces/TraceWaterfall.vue';
import TraceCorrelatedLogs from '../components/traces/TraceCorrelatedLogs.vue';
import LgButton from '../ui/LgButton.vue';
import LgIconButton from '../ui/LgIconButton.vue';
import LgInput from '../ui/LgInput.vue';
import LgSelect from '../ui/LgSelect.vue';
import LgTooltip from '../ui/LgTooltip.vue';
import GoblinIcon from '../components/GoblinIcon.vue';

const emit = defineEmits<{ unauthorized: [] }>();

// Traces refresh on the same 15s cadence as metrics — not the 2s log cadence.
const POLL_MS = 15_000;

const range = ref<TimeRange>(DEFAULT_RANGE);
const live = ref(true);
const liveTitle = computed(() =>
  live.value
    ? `Auto-refresh every ${POLL_MS / 1000}s. Click to pause.`
    : 'Auto-refresh is off. Click to resume.',
);

// --- Filters ----------------------------------------------------------------
const service = ref(''); // '' = all services
const errorsOnly = ref(false);
const minDurationText = ref('');
const userText = ref('');
const searchText = ref('');

// Text inputs are debounced so we don't refetch on every keystroke.
const DEBOUNCE_MS = 300;
const minDuration = ref('');
const user = ref('');
const search = ref('');
let debounceTimer: ReturnType<typeof setTimeout> | undefined;
watch([minDurationText, userText, searchText], () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    minDuration.value = minDurationText.value.trim();
    user.value = userText.value.trim();
    search.value = searchText.value.trim();
  }, DEBOUNCE_MS);
});

const services = ref<string[]>([]);
const serviceOptions = computed(() => [
  { value: '', label: 'All services' },
  // Keep the currently-picked service in the list even if this window has no
  // traces for it, so the select never shows a bare value.
  ...[...new Set(service.value ? [...services.value, service.value] : services.value)]
    .sort()
    .map((s) => ({ value: s, label: s })),
]);

// --- Trace list -------------------------------------------------------------
const traces = ref<TraceSummary[]>([]);
// Starts true so the pre-fetch first paint shows "Loading…", not a false
// "No traces yet" flash before the mount-time load kicks in.
const listLoading = ref(true);
const listError = ref(false);
let listSeq = 0;
let listInFlight = false;

async function loadList(silent = false) {
  if (listInFlight) return;
  listInFlight = true;
  const seq = ++listSeq;
  if (!silent) listLoading.value = traces.value.length === 0;
  try {
    const now = Date.now();
    const minMs = Number(minDuration.value);
    const res = await api.traces.list({
      from: now - RANGE_MS[range.value],
      to: now,
      service: service.value || undefined,
      userId: user.value || undefined,
      errorsOnly: errorsOnly.value || undefined,
      minDurationMs: Number.isFinite(minMs) && minMs > 0 ? minMs : undefined,
      search: search.value || undefined,
    });
    if (seq !== listSeq) return;
    traces.value = res.traces;
    services.value = res.services;
    listError.value = false;
  } catch (e) {
    if (e instanceof ApiError && e.unauthorized) {
      emit('unauthorized');
      return;
    }
    if (seq !== listSeq) return;
    // Don't clobber a visible list on a silent poll failure.
    if (!silent || !traces.value.length) listError.value = true;
  } finally {
    if (seq === listSeq) listLoading.value = false;
    listInFlight = false;
  }
}

// Refetch when any filter changes.
watch([service, errorsOnly, minDuration, user, search], () => loadList());

function setRange(r: TimeRange) {
  range.value = r;
  loadList();
}

// --- Trace detail -----------------------------------------------------------
const selected = ref<TraceSummary | null>(null);
const spans = ref<TraceSpan[]>([]);
const detailLoading = ref(false);
const detailError = ref(false);
const detailTab = ref<'waterfall' | 'logs'>('waterfall');
let detailSeq = 0;

async function loadDetail(traceId: string) {
  const seq = ++detailSeq;
  detailLoading.value = true;
  detailError.value = false;
  try {
    const res = await api.traces.get(traceId);
    if (seq !== detailSeq) return;
    spans.value = res.spans;
  } catch (e) {
    if (e instanceof ApiError && e.unauthorized) {
      emit('unauthorized');
      return;
    }
    if (seq !== detailSeq) return;
    spans.value = [];
    detailError.value = true;
  } finally {
    if (seq === detailSeq) detailLoading.value = false;
  }
}

function onPickTrace(t: TraceSummary) {
  if (selected.value?.traceId === t.traceId) return;
  selected.value = t;
  detailTab.value = 'waterfall';
  spans.value = [];
  loadDetail(t.traceId);
}
function closeDetail() {
  selected.value = null;
}

function absTime(ts: number): string {
  return new Date(ts).toLocaleString();
}

// --- Poll -------------------------------------------------------------------
let pollTimer: ReturnType<typeof setInterval> | undefined;
onMounted(() => {
  loadList();
  pollTimer = setInterval(() => {
    if (live.value) loadList(true);
  }, POLL_MS);
});
onBeforeUnmount(() => {
  clearInterval(pollTimer);
  clearTimeout(debounceTimer);
});

watch(live, (on) => {
  if (on) loadList(true);
});
</script>

<style scoped>
.traces {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
  padding: var(--lg-space-2) var(--lg-space-3);
  border-bottom: 1px solid var(--lg-border);
  flex-shrink: 0;
}
.toolbar-spacer {
  flex: 1;
  min-width: 0;
}
.live-toggle.live {
  color: var(--lg-ok);
}
.live-toggle.off {
  color: var(--lg-text-faint);
}

.filter-row {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
  padding: var(--lg-space-2) var(--lg-space-3);
  border-bottom: 1px solid var(--lg-border);
  flex-shrink: 0;
}
.service-select {
  min-width: 150px;
}
.errors-toggle.on {
  color: var(--lg-danger);
  border-color: color-mix(in srgb, var(--lg-danger) 45%, var(--lg-border-strong));
}
.min-dur {
  width: 110px;
}
.user-filter {
  width: 130px;
}
.search {
  flex: 1;
  min-width: 160px;
  max-width: 380px;
}
.search-clear {
  width: 20px;
  height: 20px;
}

.body {
  display: flex;
  flex: 1;
  min-height: 0;
}
.list-pane {
  width: 380px;
  flex-shrink: 0;
  border-right: 1px solid var(--lg-border);
  min-height: 0;
  overflow: hidden;
}
.detail-pane {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.detail-head {
  flex-shrink: 0;
  padding: var(--lg-space-3) var(--lg-space-3) 0;
}
.back-btn {
  margin-bottom: var(--lg-space-2);
}
.detail-title-row {
  display: flex;
  align-items: baseline;
  gap: var(--lg-space-3);
  min-width: 0;
  flex-wrap: wrap;
}
.detail-title {
  margin: 0;
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-lg);
  font-weight: 600;
  min-width: 0;
  overflow-wrap: anywhere;
}
.detail-when {
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
  white-space: nowrap;
}
.detail-stats {
  display: flex;
  flex-wrap: wrap;
  gap: var(--lg-space-1) var(--lg-space-4);
  margin-top: var(--lg-space-2);
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
}
.stat-k {
  color: var(--lg-text-faint);
  margin-right: var(--lg-space-2);
  text-transform: uppercase;
  font-size: var(--lg-fs-2xs);
  letter-spacing: 0.04em;
}
.stat-err {
  color: var(--lg-danger);
  font-weight: 600;
}

.detail-tabs {
  display: flex;
  gap: var(--lg-space-1);
  margin-top: var(--lg-space-3);
  border-bottom: 1px solid var(--lg-border);
}
.tab {
  font: inherit;
  font-size: var(--lg-fs-sm);
  font-weight: 600;
  padding: var(--lg-space-2) var(--lg-space-3);
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: var(--lg-text-dim);
  cursor: pointer;
}
.tab:hover {
  color: var(--lg-text);
}
.tab.active {
  color: var(--lg-text);
  border-bottom-color: var(--lg-accent);
}
.tab:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--lg-accent);
}

.detail-body {
  flex: 1;
  min-height: 0;
  min-width: 0;
  padding: var(--lg-space-3);
}
.detail-note {
  padding: var(--lg-space-3);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
}
.detail-note.err {
  color: var(--lg-danger);
}
.link-btn {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  text-decoration: underline;
  font: inherit;
}

.detail-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-6);
  color: var(--lg-text-faint);
  text-align: center;
}
.empty-title {
  margin: 0;
  font-size: var(--lg-fs-lg);
  color: var(--lg-text-dim);
}
.empty-sub {
  margin: 0;
  font-size: var(--lg-fs-sm);
  max-width: 46ch;
}

.only-mobile {
  display: none;
}
@media (max-width: 768px) {
  .only-mobile {
    display: inline-flex;
  }
  /* Single column: the list fills the screen; the detail becomes a full-width
     overlay with a back control when a trace is picked. */
  .list-pane {
    width: 100%;
    border-right: none;
  }
  .detail-pane {
    display: none;
  }
  .detail-pane.mobile-open {
    display: flex;
    position: fixed;
    inset: 0;
    z-index: var(--lg-z-drawer);
    background: var(--lg-bg);
    padding-top: env(safe-area-inset-top, 0);
  }
  .search {
    order: 5;
    flex-basis: 100%;
    max-width: none;
  }
}
</style>
